// Shared number/date formatting utilities.
// tio-ui is the base addon (tio-data, tio-common and tio-reporting all depend
// on it, not the reverse), so this is the one place every package can share a
// formatter from.

/**
 * Cached `Intl.NumberFormat` for USD amounts.
 *
 * Construction is the expensive part of `Intl`, so formatters are memoized per
 * locale + min/max fraction-digit triple rather than rebuilt per call.
 *
 * `maxFractionDigits` defaults to `fractionDigits`, so the common "exactly N
 * digits" case stays a two-argument call. Pass it explicitly for the min≠max
 * case — e.g. `(…, 0, 2)` shows cents only when they are non-zero.
 *
 * `locale` is explicit on purpose. Call sites historically disagreed — some
 * hardcoded `'en-US'`, one passed the active ember-intl locale, one passed
 * `undefined` to follow the environment — and those produce different output
 * for the es-mx and fr-ca locales the apps ship. Each caller keeps its
 * existing choice here; unifying them is a user-visible decision, not a
 * refactor. Pass `undefined` to follow the runtime default.
 *
 * In templates prefer the `safe-format-number` helper: it routes through
 * ember-intl and so tracks the active locale automatically.
 */
const currencyFormatters = new Map<string, Intl.NumberFormat>();

export function currencyFormatter(
  locale: string | undefined,
  fractionDigits = 2,
  maxFractionDigits = fractionDigits
): Intl.NumberFormat {
  const key = `${locale ?? ''}|${fractionDigits}|${maxFractionDigits}`;
  let formatter = currencyFormatters.get(key);

  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: maxFractionDigits,
    });
    currencyFormatters.set(key, formatter);
  }

  return formatter;
}

/**
 * Format a number as a US currency string (e.g., "$42,000.00").
 *
 * Two fraction digits by default; pass `fractionDigits` to override (e.g., 0
 * for whole-dollar display). Nullish and NaN amounts render as zero rather
 * than blowing up a template.
 */
export function formatCurrency(amount: number | undefined | null, fractionDigits = 2): string {
  const value = amount == null || Number.isNaN(amount) ? 0 : amount;

  return currencyFormatter('en-US', fractionDigits).format(value);
}

/**
 * Format an integer cent amount as a US currency string.
 *
 * The API returns most money as cents, so this saves each call site from
 * repeating the `/ 100`.
 */
export function formatCentsAsCurrency(
  cents: number | undefined | null,
  fractionDigits = 2
): string {
  return formatCurrency(cents == null || Number.isNaN(cents) ? 0 : cents / 100, fractionDigits);
}

/**
 * Cached `Intl.DateTimeFormat` for date display.
 *
 * The same reasoning as `currencyFormatter` applies — construction is the
 * expensive part of `Intl`, and the date helpers below were each building a
 * fresh formatter on every call, including inside `.map()` over a series.
 *
 * The cache key sorts the option entries, so two callers passing the same
 * options in a different literal order still share one formatter.
 *
 * Exported for call sites that need an option set the named helpers below
 * don't cover; prefer a named helper when one fits.
 */
const dateFormatters = new Map<string, Intl.DateTimeFormat>();

export function dateFormatter(
  locale: string | undefined,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const optionKey = Object.entries(options)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${String(v)}`)
    .join(',');
  const key = `${locale ?? ''}|${optionKey}`;
  let formatter = dateFormatters.get(key);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateFormatters.set(key, formatter);
  }

  return formatter;
}

const MONTH_YEAR_SHORT: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
const MONTH_YEAR_LONG: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
const MONTH_SHORT: Intl.DateTimeFormatOptions = { month: 'short' };
const SHORT_DATE: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};
const NUMERIC_DATE: Intl.DateTimeFormatOptions = {
  month: 'numeric',
  day: '2-digit',
  year: 'numeric',
};

/**
 * Format a date as "Jan 2025" (short month + year).
 */
export function formatMonthYear(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return dateFormatter('en-US', MONTH_YEAR_SHORT).format(d);
}

/**
 * Format a date as "January 2025" (full month + year).
 */
export function formatMonthYearLong(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return dateFormatter('en-US', MONTH_YEAR_LONG).format(d);
}

/**
 * Format a date as "Jan" (short month alone) — for dense series axis labels.
 */
export function formatMonthShort(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return dateFormatter('en-US', MONTH_SHORT).format(d);
}

/**
 * Format a date as "Jan 15, 2025" (short month + day + year).
 */
export function formatShortDate(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return dateFormatter('en-US', SHORT_DATE).format(d);
}

/**
 * Format a date as "1/15/2025" (numeric month/day/year).
 */
export function formatNumericDate(date: Date | string | undefined | null): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  return dateFormatter('en-US', NUMERIC_DATE).format(d);
}

/**
 * Format a benefit cycle string ("YYYY-MM") as "Jan 2025" (short month + year).
 *
 * Falls back to standard date parsing for non-cycle strings.
 */
export function formatCycleDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';

  // Handle "YYYY-MM" format (e.g., "2026-01")
  const dashParts = dateStr.split('-');
  if (dashParts.length >= 2) {
    const date = new Date(Number(dashParts[0]), Number(dashParts[1]) - 1);
    if (!isNaN(date.getTime())) {
      return dateFormatter('en-US', MONTH_YEAR_SHORT).format(date);
    }
  }

  // Handle accounting period "YYYYMmm" format (e.g., "2026M01")
  const periodMatch = dateStr.match(/^(\d{4})M(\d{2})$/);
  if (periodMatch) {
    const date = new Date(Number(periodMatch[1]), Number(periodMatch[2]) - 1);
    if (!isNaN(date.getTime())) {
      return dateFormatter('en-US', MONTH_YEAR_SHORT).format(date);
    }
  }

  // Fallback: try standard date parsing
  const fallback = new Date(dateStr);
  if (isNaN(fallback.getTime())) return '—';
  return dateFormatter('en-US', MONTH_YEAR_SHORT).format(fallback);
}

/**
 * Format a benefit cycle string ("YYYY-MM") as "December 2025".
 *
 * Uses the full month name followed by the year.
 * Falls back to standard date parsing for non-cycle strings.
 */
export function formatCycleDateLong(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';

  // Handle "YYYY-MM" format (e.g., "2025-12")
  const dashParts = dateStr.split('-');
  if (dashParts.length >= 2) {
    const monthNum = Number(dashParts[1]);
    if (monthNum >= 1 && monthNum <= 12) {
      const date = new Date(Number(dashParts[0]), monthNum - 1);
      if (!isNaN(date.getTime())) {
        return dateFormatter('en-US', MONTH_YEAR_LONG).format(date);
      }
    }
  }

  // Handle accounting period "YYYYMmm" format (e.g., "2025M12")
  const periodMatch = dateStr.match(/^(\d{4})M(\d{2})$/);
  if (periodMatch) {
    const monthNum = Number(periodMatch[2]);
    if (monthNum >= 1 && monthNum <= 12) {
      const date = new Date(Number(periodMatch[1]), monthNum - 1);
      if (!isNaN(date.getTime())) {
        return dateFormatter('en-US', MONTH_YEAR_LONG).format(date);
      }
    }
  }

  // Fallback: try standard date parsing
  const fallback = new Date(dateStr);
  if (isNaN(fallback.getTime())) return '—';
  return dateFormatter('en-US', MONTH_YEAR_LONG).format(fallback);
}
